import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { IntegrityCase, WhistleblowerReport } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const DECLINE_STATUSES = ['UNDER_REVIEW', 'DECLINED_INSUFFICIENT', 'DECLINED_OUT_OF_SCOPE', 'CLOSED_NO_ACTION'] as const;

/** GET /whistleblower/reports/:id — ACCS triage + convert-to-case (converting investigator becomes LEAD). */
export function WhistleblowerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reportQ = useApiQuery<WhistleblowerReport>(id ? `/api/v1/m40/whistleblower/reports/${id}` : null);
  const [showTriage, setShowTriage] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (reportQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading report…</div>;
  if (reportQ.error) return <ErrorNote message={reportQ.error} />;
  const r = reportQ.data;
  if (!r) return null;

  const canTriage = r.status !== 'CONVERTED_TO_CASE';
  const canConvert = r.status !== 'CONVERTED_TO_CASE' && !r.status.startsWith('DECLINED') && r.status !== 'CLOSED_NO_ACTION';

  return (
    <div>
      <PageHeader title={r.refNo} subtitle={r.category.replaceAll('_', ' ')} actions={<StatusBadge status={r.status} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard label="Subject MDA" value={r.subjectMda ?? '—'} />
        <StatCard label="Submitted" value={formatDateTime(r.createdAt)} />
        <StatCard label="Triaged" value={r.triagedAt ? formatDateTime(r.triagedAt) : 'Not yet'} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Alleged wrongdoer / entity</h2>
        <p className="mb-4 text-sm text-slate-700">{r.subjectDescription}</p>
        <h2 className="mb-2 text-sm font-bold text-slate-700">Narrative</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{r.narrative}</p>
        {r.triageNote ? <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Triage note: {r.triageNote}</div> : null}
        {r.convertedToIntegrityCaseId ? (
          <button className="btn-secondary mt-3 !px-2.5 !py-1 text-xs" onClick={() => navigate(`/ombudsman/integrity-cases/${r.convertedToIntegrityCaseId}`)}>
            View converted integrity case →
          </button>
        ) : null}
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        No complainant identity of any kind is stored against this report — see the module&rsquo;s design notes. Only a one-time case key (never
        persisted, only its hash) lets the anonymous reporter check status later via the public lookup form.
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {canTriage ? (
          <button className="btn-secondary" onClick={() => setShowTriage(true)}>
            Triage
          </button>
        ) : null}
        {canConvert ? (
          <button className="btn-primary" onClick={() => setShowConvert(true)}>
            Convert to integrity case (become LEAD)
          </button>
        ) : null}
      </div>

      {showTriage ? (
        <TriageModal
          onClose={() => setShowTriage(false)}
          onSubmit={async (status, note) => {
            setActionError(null);
            try {
              await api.post(`/api/v1/m40/whistleblower/reports/${r.id}/triage`, { status, note: note || undefined });
              setShowTriage(false);
              reportQ.reload();
            } catch (e) {
              setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Triage failed');
            }
          }}
        />
      ) : null}
      {showConvert ? (
        <ConvertModal
          defaultSubjectMda={r.subjectMda ?? ''}
          defaultAllegation={r.narrative}
          onClose={() => setShowConvert(false)}
          onSubmit={async (body) => {
            setActionError(null);
            try {
              const created = await api.post<IntegrityCase>(`/api/v1/m40/whistleblower/reports/${r.id}/convert-to-case`, body);
              setShowConvert(false);
              navigate(`/ombudsman/integrity-cases/${created.id}`);
            } catch (e) {
              setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Conversion failed');
            }
          }}
        />
      ) : null}
    </div>
  );
}

function TriageModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (status: (typeof DECLINE_STATUSES)[number], note: string) => Promise<void> }) {
  const [status, setStatus] = useState<(typeof DECLINE_STATUSES)[number]>('UNDER_REVIEW');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(status, note);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Triage whistleblower report" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Decision">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="UNDER_REVIEW">Keep under review</option>
            <option value="DECLINED_INSUFFICIENT">Decline — insufficient detail</option>
            <option value="DECLINED_OUT_OF_SCOPE">Decline — out of ACCS scope</option>
            <option value="CLOSED_NO_ACTION">Close — no action</option>
          </select>
        </Field>
        <Field label="Note (optional)">
          <textarea className="field-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save triage decision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ConvertModal({
  defaultSubjectMda,
  defaultAllegation,
  onClose,
  onSubmit,
}: {
  defaultSubjectMda: string;
  defaultAllegation: string;
  onClose: () => void;
  onSubmit: (body: { subjectName: string; subjectMda?: string; allegationSummary?: string }) => Promise<void>;
}) {
  const [subjectName, setSubjectName] = useState('');
  const [subjectMda, setSubjectMda] = useState(defaultSubjectMda);
  const [allegationSummary, setAllegationSummary] = useState(defaultAllegation);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({ subjectName, subjectMda: subjectMda || undefined, allegationSummary: allegationSummary || undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Convert to a formal integrity case" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">You become the case&rsquo;s LEAD investigator — add other team members afterwards from the case page.</p>
        <Field label="Subject name (the alleged wrongdoer — never the reporter)">
          <input className="field-input" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} />
        </Field>
        <Field label="Subject MDA (optional)">
          <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} />
        </Field>
        <Field label="Allegation summary">
          <textarea className="field-input" rows={3} value={allegationSummary} onChange={(e) => setAllegationSummary(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || subjectName.trim().length < 2}>
            {busy ? 'Converting…' : 'Convert to case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
